import { getAccessToken, safeLocalStorageGetItem } from '@global/helpers/general';
import {
  axiosRequestPath,
  getAxiosEffectiveHttpStatus,
  normalizePathForDisplay
} from '@global/monitoring/axios-helpers';
import { mixpanel, trackingEvents } from '@global/monitoring/mixpanel/index';
import * as Sentry from '@sentry/react';
import { logout } from '@redux/slices/auth';
import { store } from '@redux/store';
import { showSnackbar } from '@services/snackbar';
import axios, { AxiosError, AxiosInstance, AxiosRequestHeaders, AxiosResponse } from 'axios';
import { Buffer } from 'buffer';
import { routes } from './routes';

const TIMEOUT_MILLISECONDS = 10 * 60 * 1000;
const BASE_ROUTES = Object.values(routes).map((route) => route.base);

const normalizeUrlForTracking = (url: string | undefined): string => {
  if (!url) return 'unknown';
  const cleanUrl = url.split('?')[0];
  const apiIndex = cleanUrl.indexOf('/api');
  const pathAfterApi = apiIndex === -1 ? cleanUrl : cleanUrl.substring(apiIndex + 4);
  const segments = pathAfterApi.split('/').filter(Boolean);
  const baseRouteIndex = segments.findIndex((segment) => BASE_ROUTES.includes(`/${segment}`));
  if (baseRouteIndex === -1) {
    return cleanUrl;
  }
  const baseRoute = `${segments[baseRouteIndex]}`;
  return `${baseRoute}`;
};

export const baseURLs = {
  default: '', // use ONLY when base url is unknown, i.e. coming from config
  webWallet: import.meta.env.VITE_WEB_WALLET_API_URL + '/api/1.0',
  issuer: import.meta.env.VITE_ISSUER_API_URL + '/api/1.0',
  verificationV1: import.meta.env.VITE_VERIFICATION_API_URL + '/api/1.0',
  verificationWebWallet: import.meta.env.VITE_VERIFICATION_WEB_WALLET_API_URL,
  utility: import.meta.env.VITE_UTILITY_API_URL + '/api/1.0',
  notification: import.meta.env.VITE_NOTIFICATIONS_URL + '/api/1.0',
  payment: import.meta.env.VITE_PAYMENT_URL + '/api/1.0',
  verificationV2: import.meta.env.VITE_VERIFICATION_API_URL + '/api/2.0',
  fx: import.meta.env.VITE_FX_API_URL + '',
  evalApi: import.meta.env.VITE_EVAL_API_URL + ''
};
const baseURLsKeys = Object.keys(baseURLs) as Array<keyof typeof baseURLs>;
export const endpointsWithoutInterceptors = [import.meta.env.VITE_GEO_LOCATION_API];

const requestInterceptor = (request: any) => {
  const controller = new AbortController();
  const token = getAccessToken();
  const refreshToken = safeLocalStorageGetItem('refreshToken');
  let decodedToken = null;
  if (token) {
    try {
      decodedToken = JSON.parse(Buffer.from(token.split('.')[1] ?? '', 'base64').toString());
    } catch {
      decodedToken = null;
    }
  }

  const publicEndpoints: string[] = [
    routes.profile.login,
    routes.profile.base,
    routes.profile.getToken
  ];

  const currentTime = Math.ceil(new Date().getTime() / 1000);

  const condition =
    !publicEndpoints.includes(request?.url as string) &&
    !request?.url?.includes(routes.verifier.base) &&
    !request?.url?.includes('/profile/otp/') &&
    (!token || !decodedToken || !refreshToken);

  if (condition) {
    mixpanel.track(trackingEvents.axios.events.abort(normalizeUrlForTracking(request?.url)), {
      originalPath: request?.url,
      publicEndpoints,
      request,
      token,
      decodedToken,
      refreshToken,
      currentTime,
      condition
    });
    mixpanel.track(trackingEvents.auth.events.sessionExpired, {
      last_active_page: window.location.pathname,
      trigger: 'missing_token'
    });
    controller.abort();
    showSnackbar('Session Expired', 'error');
    store.dispatch(logout());
  }

  /* 
    Mixpanel request tracking removed here to reduce costs. 
    API failures are tracked in the responseErrorInterceptor.
  */

  return {
    ...request,
    signal: controller.signal
  };
};

const responseInterceptor = (response: AxiosResponse) => {
  try {
    // Only track if response status indicates an issue (non-2xx)
    if (response?.status < 200 || response?.status >= 300) {
      mixpanel.track(
        trackingEvents.axios.events.response(normalizeUrlForTracking(response?.config?.url)),
        {
          originalPath: response?.config?.url,
          method: response?.config?.method,
          baseURL: response?.config?.baseURL,
          status: response?.status,
          data: response?.data,
          params: response?.config?.params
        }
      );
    }
  } catch (e) {
    // Avoid blocking the response flow if mixpanel tracking fails
    console.error('Mixpanel response tracking failed', e);
  }

  if (response.status === 504) {
    showSnackbar('Server is down, please try again later', 'error');
  }

  if (response.status === 401) {
    store.dispatch(logout());
    // return null;
  }
  if (response.data.code === 138) {
    store.dispatch(logout());
    // navigation.navigateTo('/setup?loggedIn=true', {
    //   state: {
    //     link: window.location.href.split(window.location.origin)[1],
    //     nextState: { moveNext: true }
    //   },
    //   disableBack: true
    // });
  }
  return response;
};

function getAxiosTransactionName(error: AxiosError): string {
  const method = (error.config?.method ?? 'GET').toUpperCase();
  const path = axiosRequestPath(error.config);
  if (path === 'unknown') return `${method} (unknown)`;
  return `${method} ${path}`;
}

type AxiosSentryPhase = 'request' | 'response';

const AXIOS_SENTRY_RESPONSE_DATA_MAX_LEN = 500;

/** Avoid huge / sensitive response bodies in Sentry extras (stringify + cap length). */
function formatAxiosResponseDataForSentry(data: unknown): string {
  if (data == null) return '';
  try {
    const s = typeof data === 'string' ? data : JSON.stringify(data);
    return s.length > AXIOS_SENTRY_RESPONSE_DATA_MAX_LEN
      ? `${s.slice(0, AXIOS_SENTRY_RESPONSE_DATA_MAX_LEN)}…`
      : s;
  } catch {
    return String(data).slice(0, AXIOS_SENTRY_RESPONSE_DATA_MAX_LEN);
  }
}

function buildAxiosSentryExtras(
  error: AxiosError,
  phase: AxiosSentryPhase
): Record<string, unknown> {
  const requestConfig = error.config;
  const common = {
    url: requestConfig?.url ?? null,
    baseURL: requestConfig?.baseURL ?? null,
    method: requestConfig?.method ?? null,
    axios_code: error.code ?? null
  };
  if (phase === 'request') {
    return common;
  }
  return {
    ...common,
    http_status: getAxiosEffectiveHttpStatus(error) ?? null,
    response_status_raw: error.response?.status ?? null,
    data: formatAxiosResponseDataForSentry(error.response?.data)
  };
}

function captureAxiosErrorToSentry(error: AxiosError, phase: AxiosSentryPhase) {
  if (error.code === 'ERR_CANCELED') return;

  const currentPagePath = normalizePathForDisplay(window.location.pathname);

  Sentry.captureException(error, (scope) => {
    scope.setTransactionName(getAxiosTransactionName(error));
    scope.setTags({
      spa_route: currentPagePath,
      source: 'axios',
      phase
    });
    scope.setExtras(buildAxiosSentryExtras(error, phase));
    return scope;
  });
}

/** Use from the response error interceptor (failed HTTP response). */
function captureAxiosResponseError(error: AxiosError) {
  captureAxiosErrorToSentry(error, 'response');
}

const requestErrorInterceptor = (error: any) => {
  showSnackbar(error.message, 'error');
  if (axios.isAxiosError(error) && error.code !== 'ERR_CANCELED') {
    captureAxiosErrorToSentry(error, 'request');
  }
  // return Promise.reject(error); // or: throw error;
};

const responseErrorInterceptor = (error: AxiosError<{ code: number, message: any }>) => {
  if (endpointsWithoutInterceptors.includes(error?.config?.url)) {
    captureAxiosResponseError(error);
    return Promise.reject(error);
  }
  mixpanel.track(trackingEvents.axios.events.error(normalizeUrlForTracking(error?.config?.url)), {
    error,
    originalPath: error?.config?.url,
    status: error?.response?.status,
    message: error.message,
    errorString: error?.toString(),
    errorJSON: error?.toJSON()
  });
  if (error?.response?.status === 401) {
    captureAxiosResponseError(error);
    mixpanel.track(trackingEvents.auth.events.sessionExpired, {
      last_active_page: window.location.pathname,
      trigger: '401_unauthorized',
      original_url: error?.config?.url
    });
    showSnackbar('Session Expired', 'error');
    store.dispatch(logout());
    return null;
  }
  if (error?.response?.data?.code === 138) {
    store.dispatch(logout());
  }
  const profileNotFound =
    error?.response?.data?.code === 105 && error?.config?.url?.includes('/profile/login'); // ignore profile not found on login
  if (
    error?.response?.data?.code !== 104 &&
    !profileNotFound &&
    !error?.config?.headers?.noSnackbar
  ) {
    console.error('Intercepting error: ', error);
    // if (!error?.response?.data?.message) {
    if (error?.code !== 'ERR_CANCELED') {
      showSnackbar(error?.response?.data?.message ?? error?.message, 'error');
    }
    // }
  }
  captureAxiosResponseError(error);
  return Promise.reject(error);
};

const addInterceptors = (instance: AxiosInstance) => {
  instance.interceptors.request.use(
    (request) => requestInterceptor(request),
    requestErrorInterceptor
  );
  instance.interceptors.response.use(responseInterceptor, responseErrorInterceptor);
};

export type InstanceUnion = (typeof baseURLsKeys)[number];
const axiosInstances: Partial<Record<InstanceUnion, AxiosInstance>> = {};

const disableCacheInInstance = (key: keyof typeof baseURLs, ax: AxiosInstance) => {
  if (key !== 'fx') {
    ax.defaults.headers.common = {
      ...ax.defaults.headers.common,
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      Expires: '0'
    };
  }
  return ax;
};

baseURLsKeys.forEach((key) => {
  axiosInstances[key] = axios.create({
    baseURL: baseURLs[key],
    timeout: TIMEOUT_MILLISECONDS ?? 10000
  });
  addInterceptors(disableCacheInInstance(key, axiosInstances[key]));
});

const getAxiosHeaders = (x = {}): { headers: AxiosRequestHeaders } => {
  return {
    headers: {
      Authorization: `Bearer ${getAccessToken()}`,
      'partner-org-id': import.meta.env.VITE_PARTNER_ORG_ID ?? '',
      ...x
    }
  };
};

export { axiosInstances, getAxiosHeaders };
